// FPS API Types based on the OpenAPI spec

import type {
  EdfUploadAnalysisResponsePayload,
  PayCalendarGroupPayload,
  IndividualResponsePayload,
  IndividualChangeResponsePayload,
  ChangedDetectedInfoResponsePayload,
} from '@aslanpay/file-processing-service-client';

// Re-export the real EDF analysis types from the fps client package so the rest of the app has a
// single import path (app/types/fileProcessingService) for every FPS response shape.
export type {
  EdfUploadAnalysisResponsePayload,
  PayCalendarGroupPayload,
  IndividualResponsePayload,
  IndividualChangeResponsePayload,
  ChangedDetectedInfoResponsePayload,
};

export interface UploadedSalaryFields {
  salaryAppliesDate?: string;
  salaryCorrection?: boolean;
}

export type UploadedIndividualResponsePayload = IndividualResponsePayload & UploadedSalaryFields;

export interface SalaryChangeResponsePayload
  extends Omit<IndividualChangeResponsePayload, 'uploadEntry'> {
  uploadEntry?: UploadedIndividualResponsePayload;
}

export type FileUploadType =
  | 'EMPLOYEE_DATA_FILE'
  | 'APPROVED_EARNINGS_TIMESHEET'
  | 'RECOGNITION_CARD'
  | 'SAVINGS_PRESET_IMAGE';

// GIFTING_ONLY_EMPLOYEE_DATA_FILE and its analysis response shape exist in fps but aren't in the
// published @aslanpay/file-processing-service-client yet. These are structural stand-ins so the FE
// compiles against the older typings AND works correctly against the real API response at runtime —
// remove once the client package is bumped to a version containing them.
export type GiftingFileUploadType = FileUploadType | 'GIFTING_ONLY_EMPLOYEE_DATA_FILE';

// The upload type the EDF upload flow supports — standard or gifting-only.
export type EdfUploadType = 'EMPLOYEE_DATA_FILE' | 'GIFTING_ONLY_EMPLOYEE_DATA_FILE';

export interface GiftingJoinerPayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  startDate?: string;
  leaveDate?: string;
}

export interface GiftingIndividualChangeResponsePayload {
  individualId?: string;
  email?: string;
  changeType?: string;
  previousValue?: string;
  newValue?: string;
}

export interface GiftingEdfUploadAnalysisResponsePayload {
  uploadType: 'GIFTING_ONLY_EMPLOYEE_DATA_FILE';
  currentFileId: string;
  currentFileName: string;
  hasChanges: boolean;
  joiners?: GiftingJoinerPayload[];
  nameChanges?: GiftingIndividualChangeResponsePayload[];
  startDateChanges?: GiftingIndividualChangeResponsePayload[];
  leaverChanges?: GiftingIndividualChangeResponsePayload[];
}

/**
 * Validates and narrows an analysis response to GiftingEdfUploadAnalysisResponsePayload, rather than
 * blindly casting — a shape mismatch surfaces here as a clear error instead of silently propagating
 * to whatever renders the gifting EDF analysis data downstream.
 */
export function toGiftingEdfAnalysisResponsePayload(
  data: unknown
): GiftingEdfUploadAnalysisResponsePayload {
  const candidate = data as Partial<GiftingEdfUploadAnalysisResponsePayload> | null | undefined;

  if (
    !candidate ||
    candidate.uploadType !== 'GIFTING_ONLY_EMPLOYEE_DATA_FILE' ||
    typeof candidate.currentFileId !== 'string' ||
    typeof candidate.currentFileName !== 'string' ||
    typeof candidate.hasChanges !== 'boolean'
  ) {
    throw new Error('Unexpected shape for GiftingEdfUploadAnalysisResponsePayload');
  }

  return candidate as GiftingEdfUploadAnalysisResponsePayload;
}

export type FileUploadStatus =
  | 'UPLOAD_REQUESTED'
  | 'ANALYSIS_IN_PROGRESS'
  | 'AWAITING_CLIENT_CONFIRMATION'
  | 'CLIENT_CONFIRMED'
  | 'VALIDATION_IN_PROGRESS'
  | 'VALIDATED'
  | 'PROCESSING_IN_PROGRESS'
  | 'SUCCEEDED'
  | 'PARTIALLY_SUCCEEDED'
  | 'FAILED'
  | 'VALIDATION_FAILED'
  | 'CANCELLED'
  | 'EXPIRED';

export type FileContentType = 'text/csv';

export type AetBasis = 'HOURLY' | 'DAILY';

export interface FileUploadResponse {
  id: string;
  name: string;
  location: string;
  locationType: string;
  uploadType: FileUploadType;
  contentType: FileContentType;
  status: FileUploadStatus;
  statusReasons?: string;
  // Absent for a SAVINGS_PRESET_IMAGE in the global preset catalogue, which no company owns.
  companyId?: string;
  confirmedBy?: string;
  confirmedAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  createdBy: string;
  createdAt: string;
  cancelledAt?: string;
  cancelledBy?: string;
  version: number;
}

export interface AetMetadata {
  id: string;
  fileUploadId: string;
  payCalendarFriendlyId: string;
  payCalendarId: string;
  earningsPayCycleId: string;
  payCycleStartDate: string;
  payCycleEndDate: string;
  employeeEarningsAdded: string[];
  employeeEarningsChanged: string[];
  employeeEarningsDeleted: string[];
  version: number;
}

export interface AetEntry {
  id: string;
  individualId: string;
  payrollId: string;
  approvedEarnings?: number;
  currency: string;
  unit?: number;
  rate?: number;
  paydayPayCycleId: string;
  payday: string;
  basis?: AetBasis;
  version: number;
}

export interface ApprovedEarningsTimesheetResponse {
  fileUpload: FileUploadResponse;
  metadata: AetMetadata;
  entries: AetEntry[];
}

export interface ApprovedEarningsTimesheetSummaryResponse {
  fileUpload: FileUploadResponse;
  metadata: AetMetadata;
}

export interface UploadResponsePayload {
  id: string;
  fileName: string;
  uploadType: FileUploadType;
  url: string;
}

export interface VerifyUploadResponsePayload {
  id: string;
  status: FileUploadStatus;
}

export interface UploadAnalysisResponsePayload {
  currentFileId: string;
  currentFileName: string;
  previousFileId?: string;
  previousFileName?: string;
  numberOfEmployeeEarningsAdded: number;
  numberOfEmployeeEarningsChanged: number;
  numberOfEmployeeEarningsDeleted: number;
  earningsPayCycleId: string;
  payCycleStartDate: string;
  payCycleEndDate: string;
  payCalendarFriendlyId: string;
  payCalendarName: string;
}

export interface FileUploadCancellationRequest {
  statusReason?: string;
}

// Union types for responses
export type FileResponse = ApprovedEarningsTimesheetResponse;
export type FileSummaryItemResponse = ApprovedEarningsTimesheetSummaryResponse;
export type FileSummaryResponse = FileSummaryItemResponse[];
